import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Heart } from 'lucide-react';
import GlobalHeader from '@/components/GlobalHeader';
import ExportPanel from '@/components/medical/ExportPanel';
import MedicalTabs from '@/components/medical/MedicalTabs';
import MedicationsTab from '@/components/medical/MedicationsTab';
import SymptomsTab from '@/components/medical/SymptomsTab';
import SurgeriesTab from '@/components/medical/SurgeriesTab';
import VaccinationsTab from '@/components/medical/VaccinationsTab';
import AllergiesTab from '@/components/medical/AllergiesTab';
import LabTestsTab from '@/components/medical/LabTestsTab';
import QuestionsTab from '@/components/medical/QuestionsTab';
import HistoryTab from '@/components/medical/HistoryTab';
import ProfileTab from '@/components/medical/ProfileTab';
import AppointmentsTab from '@/components/medical/AppointmentsTab';
import PrintButton from '@/components/PrintButton';
import { toast } from 'sonner';
import APP_ICONS from '@/utils/appIcons';

// Medical module pastel theme - Soft Mint/Sage
const MEDICAL_THEME = {
  bgGradient: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 30%, #F0FDFA 60%, #F5F3FF 100%)',
  headerIconBg: '#D1FAE5',
  headerIconColor: 'text-emerald-600',
  cardBg: 'rgba(255, 255, 255, 0.85)',
  cardBorder: '#D1FAE5',
  accentLight: '#ECFDF5',
  accentDark: '#059669',
};

// Tab display names for print
const TAB_NAMES = {
  profile: 'Health Profile',
  appointments: 'Appointments',
  medications: 'Medications',
  symptoms: 'Symptoms',
  surgeries: 'Surgeries',
  vaccinations: 'Vaccinations',
  allergies: 'Allergies',
  labs: 'Lab Tests',
  questions: 'Doctor Questions',
  history: 'Medical History'
};

const MedicalPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    // Increment refresh key to force re-render of child tabs
    setRefreshKey(prev => prev + 1);
    toast.success('Data refreshed');
  }, []);

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab key={`profile-${refreshKey}`} />;
      case 'appointments':
        return <AppointmentsTab key={`appointments-${refreshKey}`} />;
      case 'medications':
        return <MedicationsTab key={`medications-${refreshKey}`} />;
      case 'symptoms':
        return <SymptomsTab key={`symptoms-${refreshKey}`} />;
      case 'surgeries':
        return <SurgeriesTab key={`surgeries-${refreshKey}`} />;
      case 'vaccinations':
        return <VaccinationsTab key={`vaccinations-${refreshKey}`} />;
      case 'allergies':
        return <AllergiesTab key={`allergies-${refreshKey}`} />;
      case 'labs':
        return <LabTestsTab key={`labs-${refreshKey}`} />;
      case 'questions':
        return <QuestionsTab key={`questions-${refreshKey}`} />;
      case 'history':
        return <HistoryTab key={`history-${refreshKey}`} />;
      default:
        return <ProfileTab key={`profile-${refreshKey}`} />;
    }
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ background: MEDICAL_THEME.bgGradient }}
      data-testid="medical-page"
    >
      <GlobalHeader
        iconSrc={APP_ICONS.medicalInfo}
        iconBgColor={MEDICAL_THEME.headerIconBg}
        iconColor={MEDICAL_THEME.headerIconColor}
        title="Medical Records"
        subtitle="Personal health information"
        onRefresh={handleRefresh}
      >
        <PrintButton title={`Medical Records - ${TAB_NAMES[activeTab]}`} />
        <ExportPanel sessionToken={localStorage.getItem('session_token')} />
      </GlobalHeader>

      {/* Print Header - Only visible when printing */}
      <div className="print-only print-header px-6 py-4">
        <div>
          <h1 className="print-title">Medical Records</h1>
          <p className="text-sm text-slate-600">{TAB_NAMES[activeTab]}</p>
        </div>
        <div className="print-date">
          Printed: {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28">
        <Card 
          className="shadow-sm backdrop-blur-sm print-card"
          style={{ 
            backgroundColor: MEDICAL_THEME.cardBg,
            borderColor: MEDICAL_THEME.cardBorder 
          }}
        >
          <CardContent className="p-3 sm:p-6">
            <MedicalTabs activeTab={activeTab} onTabChange={setActiveTab}>
              {renderTabContent()}
            </MedicalTabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MedicalPage;
